import * as React from 'react'

import { extractArticleFullName } from '../../../../../../shared/services/gear-service'
import withTranslator from '../TranslatorProvider'

const PropTypes = require('prop-types')
const axios = require('axios')

const { CancelToken } = axios
let searchCancelSource = CancelToken.source()

function debounce(func, wait, immediate) {
  let timeout
  return function(...otherArgs) {
    const context = this
    const later = function() {
      timeout = null
      if (!immediate) func.apply(context, otherArgs)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, otherArgs)
  }
}

function resultTitle(translate, key, count) {
  let k = `search.results.${key}.`
  if (count === 0) {
    k += 'none'
  } else if (count === 1) {
    k += 'singular'
  } else {
    k += 'plural'
  }
  return translate(k, { count })
}

class NavbarSearch extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: false,
      query: '',
      results: {
        fishes: [],
        users: [],
        brands: [],
        productsModels: [],
      },
    }
    this.onInputChange = this.onInputChange.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.startQuery = this.startQuery.bind(this)

    this.debounceStartQuery = debounce(
      function(q) {
        this.startQuery(q)
      },
      200,
      false
    )
  }

  onInputChange(e) {
    if (e) {
      e.preventDefault()
    }
    const query = e.target.value || ''
    this.setState(
      {
        query,
        loading: query.length > 0,
      },
      () => {
        this.debounceStartQuery(query)
      }
    )
  }

  onSubmit(e) {
    if (e) {
      e.preventDefault()
    }
    const { query } = this.state
    this.debounceStartQuery(query)
  }

  startQuery(q) {
    if (!q || q.length === 0) {
      this.setState({
        loading: false,
        results: { fishes: [], users: [] },
      })
      return
    }
    searchCancelSource.cancel()
    searchCancelSource = CancelToken.source()
    this.setState({ loading: true })
    q = encodeURIComponent(q)
    Promise.all([
      axios.get(`/api/search/users/${q}?limit=5`, { cancelToken: searchCancelSource.token }),
      axios.get(`/api/search/fishes/${q}?limit=5`, { cancelToken: searchCancelSource.token }),
      axios.get(`/api/search/brands/${q}?_perPage=5`, { cancelToken: searchCancelSource.token }),
      axios.get(`/api/search/fullProductModels/${q}?_perPage=5`, { cancelToken: searchCancelSource.token }),
    ])
      .then(([resultsUsers, resultsFishes, resultsBrands, resultsProducts]) => {
        this.setState({
          loading: false,
          results: {
            users: resultsUsers.data,
            fishes: resultsFishes.data,
            brands: resultsBrands.data,
            productsModels: resultsProducts.data,
          },
        })
      })
      .catch(function(thrown) {
        if (!axios.isCancel(thrown)) {
          throw thrown
        }
      })
  }

  render() {
    const { loading, query, results } = this.state
    const { translate } = this.props
    const isActive = query.length > 0
    const { users = [], fishes = [], brands = [], productsModels = [] } = results
    const showLoading = loading && fishes.length === 0 && users.length === 0 && brands.length === 0

    return (
      <div className={`searchbar field dropdown${isActive ? ' is-active' : ''}`}>
        <p className={`control has-icons-left ${loading ? ' is-loading' : ''}`}>
          <input
            value={query}
            onChange={this.onInputChange}
            className="input is-expanded large-on-focus is-small-tablet"
            type="text"
            placeholder={translate('search.placeholder')}
          />
          <span className="icon is-small is-left has-text-primary-blue">
            <i className="fas fa-search" />
          </span>
        </p>
        <div className="dropdown-menu" role="menu">
          <div className="dropdown-content">
            {showLoading && (
              <div className="dropdown-item is-size-7">
                <p className="has-text-weight-bold">
                  <span className="icon">
                    <i className="fas fa-circle-notch fa-spin" />
                  </span>
                  <span>{translate('search.loading')}</span>
                </p>
              </div>
            )}
            {!showLoading && (
              <div className="dropdown-item is-size-7">
                <p className="has-text-grey">
                  <span className="icon">
                    <i className="fas fa-users" />
                  </span>
                  <span>{resultTitle(translate, 'anglers', users.length)}</span>
                </p>
              </div>
            )}
            {!showLoading &&
              users.map(user => (
                <a
                  key={user.id}
                  href={`/users/${user.id}`}
                  title={`${user.firstName} ${user.lastName}`}
                  className="dropdown-item is-size-7"
                >
                  <span>{`${user.firstName} ${user.lastName}`}</span>
                </a>
              ))}
            {!showLoading && <hr className="dropdown-divider" />}
            {!showLoading && (
              <div className="dropdown-item is-size-7">
                <p className="has-text-grey">
                  <span className="icon">
                    <i className="fas fa-fish" />
                  </span>
                  <span>{resultTitle(translate, 'fishes', fishes.length)}</span>
                </p>
              </div>
            )}
            {!showLoading &&
              fishes.map(fish => (
                <a
                  key={fish.id}
                  href={`/fish/${fish.id}`}
                  title={`${fish.name} ${fish.scientificName}`}
                  className="dropdown-item is-size-7"
                >
                  <span>{fish.name}</span>
                </a>
              ))}
            {!showLoading && <hr className="dropdown-divider" />}
            {!showLoading && (
              <div className="dropdown-item is-size-7">
                <p className="has-text-grey">
                  <span className="icon">
                    <i className="fas fa-tags" />
                  </span>
                  <span>{resultTitle(translate, 'brands', brands.length)}</span>
                </p>
              </div>
            )}
            {!showLoading &&
              brands.map(brand => (
                <a
                  key={brand.id}
                  href={`/gear/brands/${brand.id}`}
                  title={`${brand.name}`}
                  className="dropdown-item is-size-7"
                >
                  <span>{brand.name}</span>
                </a>
              ))}
            {!showLoading && <hr className="dropdown-divider" />}
            {!showLoading && (
              <div className="dropdown-item is-size-7">
                <p className="has-text-grey">
                  <span className="icon">
                    <i className="fas fa-tags" />
                  </span>
                  <span>{resultTitle(translate, 'productsModels', productsModels.length)}</span>
                </p>
              </div>
            )}
            {!showLoading &&
              productsModels.map(pm => (
                <a
                  key={pm.productArticleId}
                  href={`/gear/products/${pm.productSeoName}?pa=${pm.productArticleSeoName}`}
                  title={`${pm.brandName} ${extractArticleFullName({
                    productName: pm.productName,
                    articleName: pm.productArticleName,
                    modelName: pm.productModelName,
                  })}`}
                  className="dropdown-item is-size-7"
                >
                  <span>
                    {`${pm.brandName} ${extractArticleFullName({
                      productName: pm.productName,
                      articleName: pm.productArticleName,
                      modelName: pm.productModelName,
                    })}`}
                  </span>
                </a>
              ))}
          </div>
        </div>
      </div>
    )
  }
}

NavbarSearch.propTypes = {
  translate: PropTypes.func.isRequired,
}

NavbarSearch.defaultProps = {}

module.exports = withTranslator(NavbarSearch)
